import { useEffect, useState, useContext } from 'react';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { WHITEBOARD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { isExternalShareRequest } from '@confluence/external-share-utils/entry-points/isExternalShareRequest.ts';
import { containsNetworkError } from '@confluence/network';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

import type { UseSpaceQueryResult } from './graphql/useSpace.ts';
import { WhiteboardTokenService, type DocumentType } from './services/whiteboard-token-service.ts';

export interface UseTokenServiceOptions {
	tenantId: string;
	space: Pick<UseSpaceQueryResult, 'id'>;
	referenceId?: string | null;
	contentId: string;
	shareToken?: string;
	dataSecurityPolicyPageExport: boolean | null;
	refetch?: () => void;
	documentType?: DocumentType;
	onPermissionChange?: (permissions: string[]) => void;
}

export const useTokenService = ({
	tenantId,
	space,
	referenceId,
	contentId,
	shareToken,
	dataSecurityPolicyPageExport,
	refetch,
	documentType = 'whiteboard',
	onPermissionChange,
}: UseTokenServiceOptions) => {
	const { edition } = useSessionData();
	const [tokenService] = useState(() => new WhiteboardTokenService(documentType));
	const isExternal = isExternalShareRequest();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});

	useEffect(() => {
		tokenService.onInvalidToken(() => {
			void refetch?.();
		});

		tokenService.onTokenChange((token) => {
			if (onPermissionChange) {
				const permissions = tokenService.getPermissions(token);
				onPermissionChange(permissions);
			}
		});

		tokenService.onTokenLoadError((error) => {
			if (isUnauthorizedError(error)) {
				void refetch?.();
			} else if (containsNetworkError(error)) {
				experienceTracker.abort({
					name: WHITEBOARD_EXPERIENCE,
					reason: 'Network error while fetching token',
				});
			} else if (isNotFoundError(error)) {
				experienceTracker.abort({
					name: WHITEBOARD_EXPERIENCE,
					reason: 'Requested content not found while fetching token',
				});
			} else if (tokenService.hasMissingTokenData(error.message)) {
				experienceTracker.abort({
					name: WHITEBOARD_EXPERIENCE,
					reason: 'Missing token data while fetching token',
				});
			} else {
				experienceTracker.fail({ name: WHITEBOARD_EXPERIENCE, error });
			}
		});
	}, [tokenService, refetch, experienceTracker, onPermissionChange]);

	// Update the current token whenever the space or contentId changes
	useEffect(() => {
		if (!space.id) {
			return;
		}

		tokenService.setContext({
			spaceId: space.id,
			tenantId,
			contentId,
			confluenceEdition: edition || '',
			isExternal,
			shareToken,
			dataSecurityPolicyPageExport,
			isAIEnabledForContent,
			isRovoEnabledForContent,
		});
		// For databases, always fetch token (can use contentId when referenceId is missing)
		// For whiteboards, only fetch when referenceId is available
		if (referenceId || documentType === 'database') {
			tokenService.updateToken(referenceId ?? undefined).catch(() => {
				// handled by the `onTokenLoadError` function defined in the useEffect above this
			});
		}

		return () => tokenService.resetToken();
	}, [
		tokenService,
		tenantId,
		space.id,
		referenceId,
		contentId,
		edition,
		shareToken,
		isExternal,
		dataSecurityPolicyPageExport,
		isAIEnabledForContent,
		isRovoEnabledForContent,
		documentType,
	]);

	return tokenService;
};
